import { DeleteOutlined, EditOutlined } from '@ant-design/icons'
import { Button, Divider, Table, Typography } from 'antd'
import { ColumnProps } from 'antd/lib/table'
import React, { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { DndContext, DragEndEvent } from '@dnd-kit/core'
import { BudgetDriver } from '../../../../../../types/budgetDriver/BudgetDriver'
import { useTableProps } from '../../../../../../utils/hooks'
import BudgetDriverModal from './BudgetDriverModal'
import DraggableTableRow from '../../../../../../components/Table/DraggableTableRow'
import { useDeleteBudgetDriver } from '../../../../../../features/budgetDrivers/queries/useDeleteBudgetDriver'
import { useUpdateBudgetDriverOrders } from '../../../../../../features/budgetDrivers/queries/useUpdateBudgetDriverOrders'
import { useDeleteConfirmModal } from '../../../../../../shared/modals/deleteConfirmModal'

interface BudgetDriversTableProps {
  budgetDrivers: BudgetDriver[]
  isFetching: boolean
}

const BudgetDriversTable: React.FC<BudgetDriversTableProps> = ({ budgetDrivers, isFetching }) => {
  const { t } = useTranslation()
  const { mutate: deleteBudgetDriver } = useDeleteBudgetDriver()
  const { mutate: updateOrders } = useUpdateBudgetDriverOrders()
  const deleteConfirmModal = useDeleteConfirmModal()
  const contentEl = useRef<HTMLDivElement>(null)
  const [budgetDriversDataSource, setBudgetDriversDataSource] = useState(budgetDrivers)
  const [isEditing, setIsEditing] = useState<BudgetDriver>()
  const [visible, setVisible] = useState(false)

  const tableProps = useTableProps(contentEl, 600)

  const handleEdit = (editBudgetDriver: any) => {
    setIsEditing({ ...editBudgetDriver, code: editBudgetDriver.formula.code, source: editBudgetDriver.formula.source })
    setVisible(true)
  }

  const handleCancel = () => {
    setIsEditing(undefined)
    setVisible(false)
  }

  useEffect(() => {
    setBudgetDriversDataSource(budgetDrivers)
  }, [budgetDrivers])

  const columns: ColumnProps<BudgetDriver>[] = [
    {
      key: 'key',
      width: 25
    },
    {
      title: t('global:name'),
      dataIndex: 'name',
      ellipsis: true
    },
    {
      title: t('global:code'),
      dataIndex: ['formula', 'code'],
      ellipsis: true
    },
    {
      title: t('global:formula'),
      dataIndex: ['formula', 'source'],
      ellipsis: true,
      render: text => <Typography.Text code>{text}</Typography.Text>
    },
    {
      title: t('global:actions'),
      key: 'action',
      width: 100,
      align: 'right',
      render: (record: any) => (
        <span>
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Divider type="vertical" />
          <Button
            danger
            type="text"
            size="small"
            icon={<DeleteOutlined />}
            onClick={() =>
              deleteConfirmModal({
                content: record.name,
                onOk: () => deleteBudgetDriver(record.id)
              })
            }
          />
        </span>
      )
    }
  ]

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      const activeIndex = budgetDriversDataSource.findIndex(i => i.id === active.id)
      const overIndex = budgetDriversDataSource.findIndex(i => i.id === over?.id)
      const updatedArray = arrayMove(budgetDriversDataSource, activeIndex, overIndex)
      const orderArray = updatedArray.map((row: any, index: any) => {
        return {
          id: row.id,
          order: index
        }
      })
      setBudgetDriversDataSource(updatedArray)
      updateOrders(orderArray)
    }
  }

  return (
    <>
      <BudgetDriverModal budgetDriver={isEditing} visible={visible} handleCancel={handleCancel} />
      <div ref={contentEl} style={{ margin: '32px 0' }}>
        <DndContext onDragEnd={onDragEnd}>
          <SortableContext
            // rowKey array
            items={budgetDriversDataSource.map(i => i.id)}
            strategy={verticalListSortingStrategy}
          >
            <Table
              rowKey="id"
              loading={isFetching}
              {...tableProps}
              columns={columns}
              dataSource={budgetDriversDataSource}
              pagination={false}
              components={{
                body: {
                  row: DraggableTableRow
                }
              }}
            />
          </SortableContext>
        </DndContext>
      </div>
    </>
  )
}

export default BudgetDriversTable
